import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { isEmpty } from 'lodash'
import { IconCopy, IconPencil, IconTrash } from '@tabler/icons-react'

import { Loader } from '@/components/loaders/Loader'
import { PageHeader } from '@/components/headers/PageHeader'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import { FilterForm } from '@/components/filters/FilterForm'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import { Button } from '@/components/buttons/Button'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { getOrganizationOptions } from '@/utils/helpers'
import { setTools, setToolsFilter } from '@/reducers/tools/tools.redux'
import {
  fetchTools,
  fetchToolFormOptions,
  fetchToolFieldTypes,
  deleteTool,
  duplicateTool,
} from '@/reducers/tools/tools.actions'

import { ToolCardContent } from './ToolCardContent'
import { TOOL_SOURCE_LABELS, TOOL_SOURCES, TOOL_TYPE_LABELS, TOOL_TYPES } from './tools.constants'
import { isLiveToolType, toolTypePillProps } from './tools.helpers'

import './Tools.scss'

const ToolsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { organizations } = useSelector((state) => state)
  const { tools, loading, filters, agentOptions, playbookOptions, fieldTypes } = useSelector(
    (state) => state.tools
  )
  const currentUser = useSelector((state) => state.currentUser)
  const organizationId = currentUser.organizationid
  const isBaltoAdmin = organizationId === 1
  const organizationOptions = getOrganizationOptions(organizations)
  // Retired types (look_up, notate) still exist in the database; the editor ignores them.
  const liveTools = tools.filter((tool) => isLiveToolType(tool.type))

  const getAgentName = (id) => {
    const match = agentOptions.find((option) => option.value === id)
    return match ? match.label : String(id)
  }

  const getPlaybookName = (cid) => {
    const match = playbookOptions.find((option) => option.value === cid)
    return match ? match.label : cid
  }

  const handleOrgSelect = (option, action) => {
    if (action.action === 'clear') {
      dispatch(setToolsFilter('organization_id', ''))
      dispatch(setTools([]))
    } else {
      dispatch(setToolsFilter('organization_id', option.value))
      dispatch(fetchTools(option.value))
      dispatch(fetchToolFormOptions(option.value))
    }
  }

  const handleDuplicate = async (tool) => {
    const result = await dispatch(
      duplicateTool(
        tool,
        liveTools.map((other) => other.name)
      )
    )
    if (result.ok) {
      history.push(`/tools/${result.tool.id}`)
    }
  }

  useEffect(() => {
    // Keep the org picked before opening a tool; the user's own org the first time.
    const orgId = filters.organization_id || organizationId
    dispatch(setToolsFilter('organization_id', orgId))
    dispatch(fetchTools(orgId))
    dispatch(fetchToolFormOptions(orgId))
    dispatch(fetchToolFieldTypes())
  }, [])

  return (
    <div data-testid="tools-page">
      <PageHeader
        title="Tools"
        actions={
          <Button
            brand
            data-testid="add-new-tool"
            onClick={() => history.push('/tools/create')}
            disabled={!filters.organization_id}
          >
            Add new tool
          </Button>
        }
      />

      {isBaltoAdmin && (
        <FilterForm small>
          <OrganizationFilter
            options={organizationOptions}
            value={filters.organization_id}
            onChange={handleOrgSelect}
          />
        </FilterForm>
      )}

      {loading.fetchTools && <Loader centered />}

      {!loading.fetchTools && !isEmpty(liveTools) && (
        <div className="tools-grid" data-testid="tools-list">
          {liveTools.map((tool) => (
            <Card
              key={tool.id}
              title={tool.name}
              pills={
                <div className="tool-card-pills">
                  <Pill emphasized small {...toolTypePillProps(tool.type)}>
                    {TOOL_TYPE_LABELS[tool.type]}
                  </Pill>
                  {tool.type === TOOL_TYPES.ENTITY_LOOKUP &&
                    tool.source &&
                    tool.source !== TOOL_SOURCES.WEB && (
                      <Pill emphasized small success>
                        {TOOL_SOURCE_LABELS[tool.source]}
                      </Pill>
                    )}
                </div>
              }
              buttons={
                <div className="flex-align-center small-gap">
                  <Button
                    square
                    basic
                    small
                    aria-label="Edit tool"
                    data-testid={`edit-tool-${tool.id}`}
                    tooltip="Edit tool"
                    onClick={() => history.push(`/tools/${tool.id}`)}
                  >
                    <IconPencil />
                  </Button>
                  {isBaltoAdmin && (
                    <Button
                      square
                      basic
                      small
                      aria-label="Duplicate tool"
                      data-testid={`duplicate-tool-${tool.id}`}
                      tooltip="Duplicate tool"
                      loading={loading.duplicateTool === tool.id}
                      onClick={() => handleDuplicate(tool)}
                    >
                      <IconCopy />
                    </Button>
                  )}
                  <ButtonAndFormModal
                    popup
                    icon={<IconTrash />}
                    buttonProps={{ basic: true, small: true, square: true }}
                    modalId={`tools/delete/${tool.id}`}
                    modalTitle={`Delete "${tool.name}"`}
                    modalProps={{ size: 'tiny' }}
                    form={
                      <ModalActions
                        onSubmit={() => dispatch(deleteTool(tool.id, filters.organization_id))}
                        isDelete
                      >
                        <p>{`Are you sure you want to delete "${tool.name}"? This action cannot be undone.`}</p>
                      </ModalActions>
                    }
                  />
                </div>
              }
            >
              <ToolCardContent
                tool={tool}
                fieldTypes={fieldTypes}
                getAgentName={getAgentName}
                getPlaybookName={getPlaybookName}
              />
            </Card>
          ))}
        </div>
      )}

      {!loading.fetchTools && isEmpty(liveTools) && filters.organization_id && (
        <p data-testid="no-tools-message">No tools found for this organization.</p>
      )}
    </div>
  )
}

export default ToolsPage
